import React, { useMemo, useState, useEffect, useRef } from "react";
import Sidebar from "../../Components/Sidebar";
import Layout from "../../Components/Layout";
import PaymentsTable from "../../Components/AdminPayments/PaymentsTable";
import PaymentForm from "../../Components/AdminPayments/PaymentForm";
import { useLazyQuery, useQuery, gql } from "@apollo/client";
import { Link } from "react-router-dom";

import {
  PaymentBillDateModeFilter,
  PaymentDateFilters,
  PaymentDueFilter,
  PaymentGstFilter,
  PaymentMethodFilter,
  PaymentPartyFilter,
} from "./PaymentFilters";
import PaymentSummaryCards, { PaymentSelectionCards } from "./PaymentSummaryCards";
import PaymentTabs from "./PaymentTabs";
import PaymentDashboard from "./PaymentDashboard";
import SettlementSuggestions from "./SettlementSuggestions";
import LinkedPayments from "./LinkedPayments";
import { GET_PAYMENTS, PAYMENT_METHODS } from "./paymentQueries";
import { useAuthz } from "../../auth/useAuthz";
import Modal from "../Model";
import PaymentCollectionReport from "./PaymentCollectionReport";

const GET_DEALERS = gql`
  query GetDealers {
    getDealers {
      id
      name
    }
  }
`;

const GET_EMPLOYEES = gql`
  query GetPaymentEmployees($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      employee {
        id
        fullName
      }
    }
  }
`;

const GET_ADMINS = gql`
  query GetPaymentAdmins {
    getAdminList {
      admins {
        id
        fullName
      }
    }
  }
`;

/* ---------------- DASHBOARD QUERY ---------------- */

const GET_PAYMENTS_DASHBOARD = gql`
  query GetPaymentsDashboard($data: AdminPaymentFilter) {
    getPaymentsDashboard(data: $data) {
      status
      totals {
        credit
        debit
        balance
      }
      categoryCredit {
        category
        amount
      }
      categoryDebit {
        category
        amount
      }
    }
  }
`;

const BOOKING_PAYMENT_CATEGORY = "Booking Payment";
const INVENTORY_PURCHASE_CATEGORY = "Inventory Purchase";
const ADMIN_SALARY_CATEGORY = "Admin Salary";
const PROFESSIONAL_CATEGORIES = [
  "Professional Salary",
  "Professional Advance Payment",
];

const matchesSearch = (payment, search) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;

  const amountQuery = query.replace(/[₹,\s]/g, "");

  return (
    [
      payment.payerName,
      payment.dealerName,
      payment.employeeName,
      payment.adminName,
      payment.category,
      payment.otherCategory,
      payment.paymentMethod,
      payment.description,
      payment.settlement?.reference,
      ...(payment.inventoryPayments || []).map(
        (item) => item.inventoryCategory?.name,
      ),
    ].some((value) => value?.toLowerCase().includes(query)) ||
    (amountQuery !== "" &&
      [payment.amount, payment.settlement?.amount].some(
        (amount) => amount != null && String(amount).includes(amountQuery),
      ))
  );
};

const AdminPayments = () => {
  const { can } = useAuthz();

  /* ---------------- STATE ---------------- */

  const [rows, setRows] = useState([]);
  // Set when the server sent only the newest entries of a larger ledger
  const [truncatedNote, setTruncatedNote] = useState("");

  const [activeTab, setActiveTab] = useState("all");
  // Bumped on every reload so the Linked Payments tab refetches too
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [billStartDate, setBillStartDate] = useState("");
  const [billEndDate, setBillEndDate] = useState("");
  const [billDateMode, setBillDateMode] = useState("EXCLUDE");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isPaymentDue, setIsPaymentDue] = useState("all"); // "all", true, false
  const [selectedDealer, setSelectedDealer] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isGstBill, setIsGstBill] = useState("all"); // "all", "true", "false"

  const [openModal, setOpenModal] = useState(false);
  const [showCollectionReport, setShowCollectionReport] = useState(false);
  const [editData, setEditData] = useState(null);

  const isInventoryCategory = selectedCategory === INVENTORY_PURCHASE_CATEGORY;
  const isProfessionalCategory =
    PROFESSIONAL_CATEGORIES.includes(selectedCategory);
  const isAdminCategory = selectedCategory === ADMIN_SALARY_CATEGORY;

  /* ---------------- FILTER VARIABLES ---------------- */

  const filters = {
    startDate: startDate || null,
    endDate: endDate || null,
    billStartDate: billStartDate || null,
    billEndDate: billEndDate || null,
    billDateMode,
    category: selectedCategory || null,
    isPaymentDue: isPaymentDue === "all" ? null : isPaymentDue,
    dealerName: selectedDealer || null,
    employeeId: selectedEmployee || null,
    adminId: selectedAdmin || null,
    paymentMethod: paymentMethod || null,
    isGstBill: isGstBill === "all" ? null : isGstBill === "true",
  };

  /* ---------------- PAYMENTS API ---------------- */

  const [fetchPayments, { loading: paymentsLoading, error: paymentsError }] =
    useLazyQuery(GET_PAYMENTS, { fetchPolicy: "network-only" });

  /* ---------------- DASHBOARD API ---------------- */

  const [fetchDashboard, { data: dashboardData, previousData }] = useLazyQuery(
    GET_PAYMENTS_DASHBOARD,
    {
      fetchPolicy: "network-only",
    },
  );
  const dashboard = (dashboardData || previousData)?.getPaymentsDashboard;

  const { data: dealersData } = useQuery(GET_DEALERS);
  const { data: employeesData } = useQuery(GET_EMPLOYEES, {
    variables: { data: { isDummy: false } },
    skip: !isProfessionalCategory,
  });
  const { data: adminsData } = useQuery(GET_ADMINS, { skip: !isAdminCategory });

  // Only the newest request may fill the table when filters change quickly
  const latestLoad = useRef(0);

  // Reads the result off the promise: re-running a query that returns
  // identical data wouldn't otherwise signal a change.
  const loadTable = async (data) => {
    const loadId = ++latestLoad.current;
    const res = await fetchPayments({ variables: { data } });
    const result = res?.data?.getPayments;
    const payments = result?.payments;
    if (payments && loadId === latestLoad.current) {
      setRows(payments);
      setTruncatedNote(result.truncated ? result.message : "");
    }
  };

  // The tiles and overall totals stay across every category and party, so
  // picking one never hides the others.
  const loadDashboard = ({
    category,
    dealerName,
    employeeId,
    adminId,
    ...data
  }) => {
    fetchDashboard({ variables: { data } });
  };

  const reload = (data) => {
    setRefreshKey((key) => key + 1);
    loadDashboard(data);
    if (data.category !== BOOKING_PAYMENT_CATEGORY) {
      loadTable(data);
    }
  };

  const handleApplyDates = () => {
    if (Boolean(startDate) !== Boolean(endDate)) {
      alert("Please select both payment start and end date");
      return;
    }
    if (Boolean(billStartDate) !== Boolean(billEndDate)) {
      alert("Please select both bill start and end date");
      return;
    }
    if (startDate > endDate || billStartDate > billEndDate) {
      alert("Start date cannot be after end date");
      return;
    }
    reload(filters);
  };

  useEffect(() => {
    reload(filters);
  }, []);

  const handleCategoryClick = (category) => {
    const data = {
      ...filters,
      category,
      dealerName: null,
      employeeId: null,
      adminId: null,
      // GST only applies to inventory purchases
      isGstBill:
        category === INVENTORY_PURCHASE_CATEGORY ? filters.isGstBill : null,
    };
    setSelectedCategory(category);
    setSelectedDealer("");
    setSelectedEmployee("");
    setSelectedAdmin("");
    if (category !== INVENTORY_PURCHASE_CATEGORY) {
      setIsGstBill("all");
    }
    setActiveTab("all");

    // Booking revenue comes from orders, so there's no payment list to show
    if (category !== BOOKING_PAYMENT_CATEGORY) {
      loadTable(data);
    }
    if (data.isGstBill !== filters.isGstBill) {
      loadDashboard(data);
    }
  };

  const handleDueChange = (value) => {
    setIsPaymentDue(value);
    reload({ ...filters, isPaymentDue: value === "all" ? null : value });
  };

  const handleBillDateModeChange = (value) => {
    setBillDateMode(value);
    reload({ ...filters, billDateMode: value });
  };

  const handleMethodChange = (value) => {
    setPaymentMethod(value);
    reload({ ...filters, paymentMethod: value || null });
  };

  const handleGstChange = (value) => {
    setIsGstBill(value);
    reload({
      ...filters,
      isGstBill: value === "all" ? null : value === "true",
    });
  };

  const handleDealerChange = (dealerName) => {
    setSelectedDealer(dealerName);
    loadTable({ ...filters, dealerName: dealerName || null });
  };

  const handleEmployeeChange = (employeeId) => {
    setSelectedEmployee(employeeId);
    loadTable({ ...filters, employeeId: employeeId || null });
  };

  const handleAdminChange = (adminId) => {
    setSelectedAdmin(adminId);
    loadTable({ ...filters, adminId: adminId || null });
  };

  // The dealer filter stays visible only on Linked Payments and Inventory
  // Purchase, so leaving the tab must not keep filtering the list unseen.
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== "linked" && !isInventoryCategory && selectedDealer) {
      setSelectedDealer("");
      loadTable({ ...filters, dealerName: null });
    }
  };

  const handleClearCategory = () => {
    setSelectedCategory(null);
    setSelectedDealer("");
    setSelectedEmployee("");
    setSelectedAdmin("");
    setIsGstBill("all");
    reload({
      ...filters,
      category: null,
      dealerName: null,
      employeeId: null,
      adminId: null,
      isGstBill: null,
    });
  };

  const handleClearAll = () => {
    setStartDate("");
    setEndDate("");
    setBillStartDate("");
    setBillEndDate("");
    setBillDateMode("EXCLUDE");
    setSelectedCategory(null);
    setIsPaymentDue("all");
    setSelectedDealer("");
    setSelectedEmployee("");
    setSelectedAdmin("");
    setPaymentMethod("");
    setIsGstBill("all");
    setSearch("");
    setActiveTab("all");
    reload({ billDateMode: "EXCLUDE" });
  };

  /* ---------------- TABLE DATA ---------------- */

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (payment) =>
          (activeTab === "all" || payment.type?.toLowerCase() === activeTab) &&
          matchesSearch(payment, search),
      ),
    [rows, activeTab, search],
  );

  const isLinkedTab = activeTab === "linked";
  const isBookingCategory =
    !isLinkedTab && selectedCategory === BOOKING_PAYMENT_CATEGORY;
  const isNarrowedDown = Boolean(
    (selectedCategory && !isBookingCategory) ||
      selectedDealer ||
      selectedEmployee ||
      selectedAdmin ||
      search.trim(),
  );

  const apiTotals = dashboard?.totals;
  const totals = {
    credit: apiTotals?.credit || 0,
    debit: apiTotals?.debit || 0,
    total: apiTotals?.balance || 0,
  };

  /* ---------------- UI ---------------- */

  return (
    <div className="min-h-screen flex">
          <Sidebar />

          <Layout>
            <div className="flex flex-col-reverse gap-4 mb-6">
              <div className="flex flex-wrap gap-4 items-end">
                <PaymentDateFilters
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  billStartDate={billStartDate}
                  billEndDate={billEndDate}
                  setBillStartDate={setBillStartDate}
                  setBillEndDate={setBillEndDate}
                  applyDateFilter={handleApplyDates}
                />
                <PaymentBillDateModeFilter
                  value={billDateMode}
                  onChange={handleBillDateModeChange}
                />
                <PaymentDueFilter
                  isPaymentDue={isPaymentDue}
                  onChange={handleDueChange}
                />
                <PaymentMethodFilter
                  value={paymentMethod}
                  onChange={handleMethodChange}
                  methods={PAYMENT_METHODS}
                />
                {isInventoryCategory && (
                  <PaymentGstFilter
                    value={isGstBill}
                    onChange={handleGstChange}
                  />
                )}
                <button
                  onClick={handleApplyDates}
                  className="border border-[#FBB033] text-[#FBB033] px-4 py-2 rounded h-10"
                >
                  Refresh
                </button>
                <button
                  onClick={handleClearAll}
                  className="border border-gray-400 text-gray-200 px-4 py-2 rounded h-10"
                >
                  Clear All
                </button>
              </div>

              <div className="flex flex-wrap gap-2 sm:justify-end">
                <SettlementSuggestions
                  canLink={can("payments.edit")}
                  onLinked={() => reload(filters)}
                />
                <Link
                  to="/admin/payments/dealers"
                  className="px-5 py-2 border border-[#FBB033] text-[#FBB033] rounded-md font-semibold whitespace-nowrap hover:bg-[#FBB033] hover:text-black transition"
                >
                  Dealer Ledger
                </Link>
                <button
                  onClick={() => setShowCollectionReport(true)}
                  className="px-5 py-2 border border-[#FBB033] text-[#FBB033] rounded-md font-semibold whitespace-nowrap hover:bg-[#FBB033] hover:text-black transition"
                >
                  Collection Report
                </button>
                {can("payments.create") && (
                  <button
                    onClick={() => {
                      setOpenModal(true);
                      setEditData(null);
                    }}
                    className="px-5 py-2 bg-[#FBB033] text-black rounded-md font-semibold whitespace-nowrap"
                  >
                    + Add Payment
                  </button>
                )}
              </div>
            </div>

            {/* SUMMARY CARDS */}

            {isNarrowedDown && !isLinkedTab ? (
              <PaymentSelectionCards rows={visibleRows} />
            ) : (
              <PaymentSummaryCards totals={totals} />
            )}

            <PaymentTabs activeTab={activeTab} setActiveTab={handleTabChange} />

            {/* CATEGORY DASHBOARD */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <PaymentDashboard
                title="Credit Categories"
                data={dashboard?.categoryCredit || []}
                color="green"
                onCategoryClick={handleCategoryClick}
              />

              <PaymentDashboard
                title="Debit Categories"
                data={dashboard?.categoryDebit || []}
                color="red"
                onCategoryClick={handleCategoryClick}
              />
            </div>

            {/* MAIN DASHBOARD */}

            <div>
              {/* PAYMENTS TABLE */}

              <div>
                {selectedCategory && (
                  <div className="mb-4 flex items-center gap-3">
                    <span className="text-[#FBB033] font-semibold">
                      Filtered by category:
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded border border-gray-200">
                      {selectedCategory}
                    </span>
                    <button
                      onClick={handleClearCategory}
                      className="text-gray-300 hover:text-white transition"
                    >
                      Clear
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-4 mb-4 items-end">
                  <div className="flex-1 min-w-[220px]">
                    <label className="text-xs text-gray-500 block mb-1">
                      Search
                    </label>
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={
                        isLinkedTab
                          ? "Dealer, UTR, payer or amount"
                          : "Dealer, name, item, description, UTR or amount"
                      }
                      className="w-full border rounded-lg px-3 py-2 bg-white text-gray-800 focus:ring-2 focus:ring-indigo-400 outline-none"
                    />
                  </div>
                  {(isInventoryCategory || isLinkedTab) && (
                    <PaymentPartyFilter
                      label="Dealer"
                      allLabel="All Dealers"
                      selected={selectedDealer}
                      onChange={handleDealerChange}
                      options={(dealersData?.getDealers || []).map((dealer) => ({
                        value: dealer.name,
                        label: dealer.name,
                      }))}
                    />
                  )}
                  {isProfessionalCategory && (
                    <PaymentPartyFilter
                      label="Professional"
                      allLabel="All Professionals"
                      selected={selectedEmployee}
                      onChange={handleEmployeeChange}
                      options={(employeesData?.getEmployeelist?.employee || []).map(
                        (employee) => ({
                          value: employee.id,
                          label: employee.fullName,
                        }),
                      )}
                    />
                  )}
                  {isAdminCategory && (
                    <PaymentPartyFilter
                      label="Admin"
                      allLabel="All Admins"
                      selected={selectedAdmin}
                      onChange={handleAdminChange}
                      options={(adminsData?.getAdminList?.admins || []).map(
                        (admin) => ({
                          value: admin.id,
                          label: admin.fullName,
                        }),
                      )}
                    />
                  )}
                </div>

                {isBookingCategory && (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                    To see booking payments, go to the{" "}
                    <Link
                      to="/admin/dashboard"
                      className="font-semibold text-blue-600 underline"
                    >
                      Dashboard
                    </Link>
                    .
                  </div>
                )}

                {!isBookingCategory && !paymentsError && truncatedNote && (
                  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                    {truncatedNote}
                  </div>
                )}

                {isLinkedTab && (
                  <LinkedPayments
                    filters={filters}
                    search={search}
                    refreshKey={refreshKey}
                    onChanged={() => reload(filters)}
                  />
                )}

                {!isLinkedTab && !isBookingCategory && paymentsError && (
                  <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-red-600">
                    Error fetching payments.
                  </div>
                )}

                {!isLinkedTab &&
                  !isBookingCategory &&
                  !paymentsError &&
                  (paymentsLoading || visibleRows.length > 0) && (
                  <PaymentsTable
                    data={visibleRows}
                    setOpenModal={setOpenModal}
                    setEditData={setEditData}
                    paymentsLoading={paymentsLoading}
                    onDeleteSuccess={() => reload(filters)}
                    category={selectedCategory}
                  />
                )}

                {!isLinkedTab &&
                  !isBookingCategory &&
                  !paymentsError &&
                  !paymentsLoading &&
                  visibleRows.length === 0 && (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
                    No payments found for the selected filters.
                  </div>
                )}

                {showCollectionReport && (
                  <Modal
                    onClose={() => setShowCollectionReport(false)}
                    widthClass="sm:w-[1100px]"
                  >
                    <PaymentCollectionReport
                      onClose={() => setShowCollectionReport(false)}
                    />
                  </Modal>
                )}

                {openModal && (
                  <Modal
                    onClose={() => setOpenModal(false)}
                    disableOutsideClick={true}
                  >
                    <PaymentForm
                      setOpenModal={setOpenModal}
                      existingPayment={editData}
                      onSuccess={() => reload(filters)}
                    />
                  </Modal>
                )}
              </div>
            </div>
          </Layout>
    </div>
  );
};

export default AdminPayments;
